import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";

interface Props {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  align?: "left" | "right";
  width?: string;
  direction?: "bottom" | "top";
  fullWidth?: boolean;
  usePortal?: boolean;
}

export default function Dropdown({
  open,
  onClose,
  children,
  className,
  align = "right",
  width = "w-max",
  direction = "bottom",
  fullWidth = false,
  usePortal = true,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width?: number } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);

  const [trigger, ...items] = Array.isArray(children) ? children : [children];

  useEffect(() => {
    if (!usePortal) return;

    if (!open || !containerRef.current) {
      setPosition(null);
      setIsPositioned(false);
      return;
    }

    let aborted = false;
    let innerRafId: number | undefined;
    let retryRafId: number | undefined;

    const resizeObserver = new ResizeObserver(() => {
      if (!aborted) updatePosition();
    });

    function updatePosition() {
      if (aborted || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current?.getBoundingClientRect();
      const dropdownHeight = dropdownRect?.height || 0;
      const dropdownWidth = dropdownRect?.width || rect.width;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let top = 0;
      let left = 0;

      const spaceBelow = viewportHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const effectiveDirection =
        direction === "top"
          ? "top"
          : spaceBelow < dropdownHeight && spaceAbove > spaceBelow
            ? "top"
            : "bottom";

      if (effectiveDirection === "bottom") {
        top = rect.bottom + 8;
      } else {
        top = rect.top - dropdownHeight - 8;
      }

      if (align === "right") {
        left = rect.right - dropdownWidth;
      } else {
        left = rect.left;
      }

      left = Math.max(8, Math.min(left, viewportWidth - dropdownWidth - 8));
      top = Math.max(8, Math.min(top, viewportHeight - dropdownHeight - 8));

      setPosition({ top, left, width: fullWidth ? rect.width : undefined });
    }

    function showWithObserver() {
      updatePosition();
      setIsPositioned(true);
      if (dropdownRef.current) {
        resizeObserver.observe(dropdownRef.current);
      }
    }

    updatePosition();
    const outerRafId = requestAnimationFrame(() => {
      if (aborted) return;
      innerRafId = requestAnimationFrame(() => {
        if (aborted) return;
        if (dropdownRef.current) {
          showWithObserver();
        } else {
          retryRafId = requestAnimationFrame(() => {
            if (!aborted) showWithObserver();
          });
        }
      });
    });

    function handleScroll() {
      updatePosition();
    }
    function handleResize() {
      updatePosition();
    }

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      aborted = true;
      cancelAnimationFrame(outerRafId);
      if (innerRafId !== undefined) cancelAnimationFrame(innerRafId);
      if (retryRafId !== undefined) cancelAnimationFrame(retryRafId);
      resizeObserver.disconnect();
      setIsPositioned(false);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, align, direction, fullWidth, usePortal]);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (target instanceof Element && target.closest("[data-ui-dropdown-panel]")) return;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  const panelClass =
    "rounded-lg background-static-white p-1 z-[10100] shadow-[0_6px_12px_0_var(--color-alpha-black-05),0_1px_1px_0_var(--color-alpha-black-10),0_0_0_1px_var(--color-alpha-black-10)]";

  const dropdownContent = open && position && (
    <div
      ref={dropdownRef}
      data-ui-dropdown-panel
      className={cn(
        "fixed",
        panelClass,
        isPositioned && "animate-fadeIn",
        !position.width && width,
        className,
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        visibility: isPositioned ? "visible" : "hidden",
        ...(position.width && { width: `${position.width}px` }),
      }}>
      {items}
    </div>
  );

  const staticPositionClass = cn(
    direction === "bottom" ? "top-full mt-2" : "bottom-full mb-2",
    align === "right" ? "right-0" : "left-0",
  );

  const inlineDropdownContent = open && (
    <div
      ref={dropdownRef}
      className={cn(
        "absolute animate-fadeIn",
        panelClass,
        staticPositionClass,
        fullWidth ? "w-full min-w-0" : width,
        className,
      )}>
      {items}
    </div>
  );

  return (
    <>
      <div ref={containerRef} className={cn("relative", fullWidth ? "w-full min-w-0" : "inline-block")}>
        {trigger}
        {!usePortal && inlineDropdownContent}
      </div>
      {usePortal && typeof document !== "undefined" && dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}
